import React, { FC } from "react";
import { PlusIcon } from "@phosphor-icons/react";
import { CustomBadge } from "../../../shared/CustomBadge/CustomBadge";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import styles from "../ProfilingProtocolDetailPage.module.scss";

type VerdettoTone = "good" | "warn" | "neutral";

/** Tono verdetto → colore CustomBadge (design system condiviso). */
const VERDETTO_BADGE_COLOR: Record<VerdettoTone, "lightgreen" | "lightred" | "light-blue-dust"> = {
  good: "lightgreen",
  warn: "lightred",
  neutral: "light-blue-dust",
};

interface IProfileHistoryListProps {
  profiles: any[];
  outputType?: "regression" | "fv_jump" | "fv_sprint" | "index";
  /** Id profilo attualmente selezionato per navigazione cronologia (sez.5). */
  selectedProfileId?: string | null;
  /** Callback per selezionare un profilo dalla cronologia. */
  onSelectProfile?: (profileId: string) => void;
  /** Apre il flusso di inserimento nuova profilazione (sez.5). */
  onAddNew?: () => void;
}

/**
 * Verdetto sintetico a colori (spec Profiling sez.5.2): lettura automatica della
 * KPI principale del protocollo.
 *  - F-V (salto/sprint): direzione del FVimb (deficit forza vs velocità)
 *  - regression (VBT): affidabilità della stima via R²
 *  - index (es. P8 Nordic): asimmetria via asymmetryPct/asymmetryRisk
 */
function getVerdetto(
  outputType: IProfileHistoryListProps["outputType"],
  outputs: Record<string, any> | undefined,
): { label: string; tone: VerdettoTone } | null {
  if (!outputs) return null;

  if (outputType === "fv_jump" || outputType === "fv_sprint") {
    const imb = outputs.FVimb;
    if (imb == null) return null;
    if (Math.abs(imb) < 10) return { label: "Profilo bilanciato", tone: "good" };
    return imb > 0
      ? { label: `Deficit velocità (+${imb}%)`, tone: "warn" }
      : { label: `Deficit forza (${imb}%)`, tone: "warn" };
  }

  if (outputType === "regression") {
    const r2 = outputs.R2;
    if (r2 == null) return null;
    return r2 >= 0.95
      ? { label: "Stima affidabile (R²≥0,95)", tone: "good" }
      : { label: "Stima incerta (R²<0,95)", tone: "warn" };
  }

  /* sez.5.2 — index: usa asimmetria se presente (P8 Nordic Hamstring e simili) */
  if (outputType === "index") {
    const risk = outputs.asymmetryRisk as string | undefined;
    const pct = outputs.asymmetryPct as number | undefined;

    if (risk != null) {
      const riskLower = risk.toLowerCase();
      if (riskLower === "low" || riskLower === "basso") {
        return { label: "Simmetria OK", tone: "good" };
      }
      if (riskLower === "high" || riskLower === "alto") {
        const suffix = pct != null ? ` (${pct}%)` : "";
        return { label: `Asimmetria elevata${suffix}`, tone: "warn" };
      }
      if (riskLower === "moderate" || riskLower === "medio") {
        const suffix = pct != null ? ` (${pct}%)` : "";
        return { label: `Asimmetria moderata${suffix}`, tone: "neutral" };
      }
    }
    if (pct != null) {
      if (pct < 10) return { label: "Simmetria OK", tone: "good" };
      if (pct >= 15) return { label: `Asimmetria elevata (${pct}%)`, tone: "warn" };
      return { label: `Asimmetria moderata (${pct}%)`, tone: "neutral" };
    }
    return null;
  }

  return null;
}

const HIDDEN_KEYS = new Set(["n", "intercept", "slope", "velocityZones"]);

/** Estrae fino a 3 KPI numeriche significative dagli outputs. */
function topKpis(outputs: Record<string, any> | undefined): [string, number][] {
  if (!outputs) return [];
  return Object.entries(outputs)
    .filter(([k, v]) => typeof v === "number" && !HIDDEN_KEYS.has(k))
    .slice(0, 3) as [string, number][];
}

/**
 * Cronologia delle profilazioni (spec Profiling sez.5.1): una card per profilazione,
 * ordinata dalla più recente, con data + verdetto a colori + KPI estratte.
 * Le card sono cliccabili: selezione sincronizza il dettaglio in alto.
 * Warning R² basso visibile direttamente sulla card (sez.8).
 */
export const ProfileHistoryList: FC<IProfileHistoryListProps> = ({
  profiles,
  outputType,
  selectedProfileId,
  onSelectProfile,
  onAddNew,
}) => {
  if (!profiles || profiles.length === 0) return null;

  return (
    <div className={styles.card}>
      <div className={styles.sectionHeader}>
        <p className={styles.historyTitle}>
          Cronologia profilazioni ({profiles.length})
        </p>
        {/* sez.5 — bottone Nuova profilazione */}
        {onAddNew && (
          <CustomButton
            color="primary"
            size="sm"
            iconPosition="left"
            icon={<PlusIcon size={14} weight="bold" />}
            onClick={onAddNew}
          >
            Nuova profilazione
          </CustomButton>
        )}
      </div>
      <div className={styles.historyList}>
        {profiles.map((p, idx) => {
          const verdetto = getVerdetto(outputType, p.outputs);
          const kpis = topKpis(p.outputs);
          const isLatest = idx === 0;
          const isSelected = selectedProfileId
            ? p._id === selectedProfileId
            : isLatest;

          /* sez.8 — warning R² basso sulla card storica */
          const r2 = p.outputs?.R2;
          const hasLowR2 = r2 != null && r2 < 0.95;

          return (
            <div
              key={p._id ?? idx}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              className={[
                styles.historyCard,
                isLatest ? styles.historyCardLatest : "",
                isSelected ? styles.historyCardSelected : "",
                onSelectProfile ? styles.historyCardClickable : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelectProfile?.(p._id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectProfile?.(p._id);
                }
              }}
            >
              <div className={styles.historyCardTop}>
                <span className={styles.historyDate}>
                  {p.sessionDate
                    ? new Date(p.sessionDate).toLocaleDateString("it-IT")
                    : "—"}
                  {isLatest && (
                    <span className={styles.historyLatestTag}>ultima</span>
                  )}
                </span>
                <div className={styles.historyBadges}>
                  {verdetto && (
                    <CustomBadge
                      label={verdetto.label}
                      color={VERDETTO_BADGE_COLOR[verdetto.tone]}
                    />
                  )}
                  {/* sez.8 — badge R² basso su card storica */}
                  {hasLowR2 && (
                    <CustomBadge
                      label={`R²=${Number(r2).toFixed(2)}`}
                      color="lightred"
                    />
                  )}
                </div>
              </div>
              {kpis.length > 0 && (
                <div className={styles.historyKpis}>
                  {kpis.map(([k, v]) => (
                    <span key={k} className={styles.historyKpi}>
                      <span className={styles.historyKpiLabel}>{k}</span>
                      <span className={styles.historyKpiValue}>{v}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProfileHistoryList;
